'use client';

import React from 'react';
import {
    ArrowUpRight,
    ArrowDownRight,
    Banknote,
    CreditCard,
    TrendingUp,
    Users,
    GraduationCap,
    Bell,
    ChevronRight,
    PieChart as PieIcon,
    BarChart3
} from 'lucide-react';
import StatsCard from '@/components/sekolah/dashboard/StatsCard';
import FinancialChart from '@/components/sekolah/dashboard/FinancialChart';

export default function SchoolDashboard() {
    // School-specific data (1 School only)
    const schoolName = "PAUD Al-Azhar 1";

    const stats = [
        {
            label: 'Total Pendapatan',
            value: 'Rp 80.000.000',
            change: '+12.5%',
            icon: ArrowUpRight,
            color: 'success' as const,
        },
        {
            label: 'Pengeluaran Operasional',
            value: 'Rp 32.000.000',
            change: '-5.2%',
            icon: ArrowDownRight,
            color: 'error' as const,
        },
        {
            label: 'Saldo Kas',
            value: 'Rp 150.000.000',
            icon: Banknote,
            color: 'primary' as const,
        },
        {
            label: 'Piutang SPP Siswa',
            value: 'Rp 12.000.000',
            change: '+2.1%',
            icon: CreditCard,
            color: 'warning' as const,
        },
    ];

    const notifeAproval = [
        { title: 'Pengajuan Dana ATK', amount: 'Rp 2.500.000', time: '10m Lalu', status: 'Pending' },
        { title: 'Gaji Guru Honorer', amount: 'Rp 15.000.000', time: '1j Lalu', status: 'Urgent' },
        { title: 'Perbaikan Atap Kantin', amount: 'Rp 8.000.000', time: '3j Lalu', status: 'Pending' },
        { title: 'Pengajuan Dana ATK', amount: 'Rp 2.500.000', time: '10m Lalu', status: 'Pending' },
        { title: 'Gaji Guru Honorer', amount: 'Rp 15.000.000', time: '1j Lalu', status: 'Urgent' },
        { title: 'Perbaikan Atap Kantin', amount: 'Rp 8.000.000', time: '3j Lalu', status: 'Pending' },
        { title: 'Pengajuan Dana ATK', amount: 'Rp 2.500.000', time: '10m Lalu', status: 'Pending' },
        { title: 'Gaji Guru Honorer', amount: 'Rp 15.000.000', time: '1j Lalu', status: 'Urgent' },
        { title: 'Perbaikan Atap Kantin', amount: 'Rp 8.000.000', time: '3j Lalu', status: 'Pending' },
    ]

    return (
        <div className="p-6 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-20 animate-in fade-in duration-500">
            {/* Page Header Area */}
            <div className="bg-white border border-border-main px-8 py-10 rounded-4xl flex flex-col md:flex-row justify-between items-start md:items-end gap-4 shadow-sm">
                <div className="space-y-1">
                    <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
                        Dashboard <span className="text-primary">Kepsek</span>
                    </h1>
                    <p className="text-secondary font-bold uppercase tracking-[0.2em] text-[10px]">
                        Monitoring Unit • {schoolName}
                    </p>
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                    <button className="flex-1 md:flex-none px-6 py-3 bg-white border border-border-main rounded-2xl text-[11px] font-bold uppercase tracking-widest hover:bg-primary-soft hover:text-primary transition-all shadow-sm">
                        Laporan Bulanan
                    </button>
                    <button className="flex-1 md:flex-none px-6 py-3 gradient-green text-white rounded-2xl text-[11px] font-bold uppercase tracking-widest hover:shadow-xl hover:shadow-primary/20 transition-all">
                        Approval Baru (3)
                    </button>
                </div>
            </div>

            {/* 4 Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
                {stats.map((stat, idx) => (
                    <StatsCard key={idx} {...stat} />
                ))}
            </div>

            {/* Main Dashboard Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* Visual Charts Section (Center/Left) */}
                <div className="lg:col-span-8 space-y-8">
                    {/* Tren Arus Kas */}
                    <FinancialChart />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Status SPP */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 flex flex-col items-center justify-between text-center relative overflow-hidden h-[380px]">
                            <div className="w-full text-left mb-4">
                                <h4 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-2">
                                    <PieIcon className="w-4 h-4 text-primary" /> Status Pembayaran SPP
                                </h4>
                            </div>

                            <div className="relative w-40 h-40">
                                <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#ebf3ef" strokeWidth="3" />
                                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="var(--color-primary)" strokeWidth="3" strokeDasharray="75, 100" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-2xl font-black text-foreground">75%</span>
                                    <span className="text-[9px] font-bold text-muted uppercase">Sudah Bayar</span>
                                </div>
                            </div>

                            <div className="w-full grid grid-cols-2 gap-4 mt-4">
                                <div className="p-3 bg-primary-soft/50 rounded-2xl">
                                    <p className="text-[9px] font-bold text-muted uppercase">Lunas</p>
                                    <p className="text-sm font-black text-primary">450 Siswa</p>
                                </div>
                                <div className="p-3 bg-error-soft/50 rounded-2xl">
                                    <p className="text-[9px] font-bold text-muted uppercase">Belum</p>
                                    <p className="text-sm font-black text-error">150 Siswa</p>
                                </div>
                            </div>
                        </div>

                        {/* SDM Summary */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 flex flex-col justify-between h-[380px]">
                            <h4 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-2 mb-6">
                                <Users className="w-4 h-4 text-primary" /> SDM & Guru
                            </h4>

                            <div className="space-y-4">
                                {[
                                    { label: 'Total Guru', value: '42 Orang', icon: Users, color: 'primary' },
                                    { label: 'Total Staff', value: '12 Orang', icon: GraduationCap, color: 'info' },
                                    { label: 'Kehadiran Hari Ini', value: '98%', icon: TrendingUp, color: 'success' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 bg-background/40 border border-border-main/50 rounded-2xl hover:border-primary/30 transition-all cursor-pointer group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center shadow-sm">
                                                <item.icon className="w-4 h-4 text-muted group-hover:text-primary" />
                                            </div>
                                            <span className="text-xs font-bold text-secondary">{item.label}</span>
                                        </div>
                                        <span className="text-sm font-black text-foreground">{item.value}</span>
                                    </div>
                                ))}
                            </div>

                            <button className="w-full mt-6 py-3 border border-dashed border-border-main rounded-2xl text-[10px] font-black text-muted uppercase hover:border-primary hover:text-primary transition-all">
                                Lihat Seluruh SDM
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column: Notifications */}
                <div className="lg:col-span-4 space-y-8">
                    <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 h-[450px] flex flex-col relative overflow-hidden shadow-sm">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-3xl -mr-10 -mt-10"></div>

                        <div className="flex justify-between items-center mb-8 relative">
                            <h4 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-2">
                                <Bell className="w-4 h-4 text-primary animate-bounce-slow" /> Notifikasi Approval
                            </h4>
                            <span className="bg-error text-white text-[9px] font-black px-2 py-1 rounded-full">3 Baru</span>
                        </div>

                        <div className="space-y-4 overflow-y-auto custom-scrollbar pr-2 max-h-[300px] w-full block">
                            {notifeAproval.map((note, i) => (
                                <div key={i} className="group relative p-5 bg-background/60 border border-border-main/50 rounded-3xl hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase ${note.status === 'Urgent' ? 'bg-error-soft text-error' : 'bg-warning-soft text-warning'}`}>
                                            {note.status}
                                        </span>
                                        <span className="text-[9px] font-bold text-muted">{note.time}</span>
                                    </div>
                                    <h5 className="text-sm font-black text-foreground group-hover:text-primary transition-colors">{note.title}</h5>
                                    <p className="text-[10px] text-muted font-bold uppercase tracking-widest mt-1">{note.amount}</p>

                                    <div className="mt-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button className="flex-1 py-2 bg-primary text-white text-[9px] font-black rounded-xl hover:bg-primary-dark transition-colors">SETUJUI</button>
                                        <button className="flex-1 py-2 bg-surface border border-border-main text-secondary text-[9px] font-black rounded-xl hover:bg-error-soft hover:text-error transition-all">TOLAK</button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className="mt-8 w-full group flex items-center justify-center gap-2 text-[10px] font-black text-primary uppercase tracking-[0.2em]">
                            Lihat Semua Approval <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}